// "Ready for offline use" beside the sync indicator (REQ-UX-06, BD-56): whether places, open invoices
// and animals are on this phone, and when each was downloaded, and whether the app itself is installed for
// offline use (BD-71). A phone that has never synced, or whose app is still installing, says so.
import { useState } from "react";
import { CircleAlert, CircleCheck } from "lucide-react";
import { formatSADateTime, relativeAge } from "@rk/shared";
import { Sheet } from "../ui/sheet";
import { cx } from "../ui/primitives";
import { useOfflineReadiness } from "./readiness";
import { NatureScene } from "../brand/Brand";

export function ReadinessIndicator() {
  const r = useOfflineReadiness();
  const [open, setOpen] = useState(false);
  if (!r) return null;
  const label = r.ready ? "Ready for offline use" : "Not ready for offline use";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="offline-readiness" data-ready={r.ready}
        className={cx("flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-full border px-3 text-[13px] font-medium",
          r.ready ? "border-success/30 bg-success/10 text-[rgb(var(--c-success-text))]" : "border-warning/40 bg-warning/15 text-[rgb(var(--c-warning-text))]")}>
        {r.ready ? <CircleCheck className="h-4 w-4" aria-hidden /> : <CircleAlert className="h-4 w-4" aria-hidden />}
        {/* On a phone the ready state is the icon alone; "not ready" is always spelled out. */}
        <span className={r.ready ? "sr-only sm:not-sr-only" : ""}>{r.ready ? "Ready offline" : "Not ready offline"}</span>
        <span className="sr-only">: {label.toLowerCase()}, show details</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen} title={label}
        description={r.ready ? "This phone has what it needs to work without signal. It refreshes at every sync."
          : "This phone has not downloaded everything it needs to work without signal. Open the app with signal and wait for the sync to finish."}>
        <NatureScene className="mx-auto mb-2 block aspect-[855/296] w-44" />
        <ul className="divide-y divide-line/70" data-testid="offline-readiness-details">
          {r.groups.map((g) => (
            <li key={g.key} className="flex items-center gap-3 py-3">
              {g.at ? <CircleCheck className="h-5 w-5 text-[rgb(var(--c-success-text))]" aria-hidden /> : <CircleAlert className="h-5 w-5 text-[rgb(var(--c-warning-text))]" aria-hidden />}
              <span className="flex-1 font-medium">{g.label}</span>
              <span className="text-[14px] text-muted">{g.at ? <time dateTime={g.at} title={formatSADateTime(g.at)}>{g.key === "app" ? "Installed" : "Downloaded"} {relativeAge(g.at)}</time>
                : g.key === "app" ? "Still installing" : "Not downloaded yet"}</span>
            </li>
          ))}
        </ul>
      </Sheet>
    </>
  );
}
